import SearchIcon from '@mui/icons-material/Search';
import React, { useState } from 'react';

import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Link,
  Chip,
  useTheme,
  Box,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';

import { JiraIssueSummary } from '../../types';

const ITEMS_PER_PAGE = 20;

interface MyIssuesTableProps {
  issues: JiraIssueSummary[];
  issueCounts: {
    total: number;
    toDo: number;
    inProgress: number;
    inReview: number;
  };
}

export const MyIssuesTable = ({ issues, issueCounts }: MyIssuesTableProps) => {
  const theme = useTheme();
  const toDoSummaryColor = 'rgba(107, 193, 255, 0.95)';
  const inProgressSummaryColor = 'rgba(224, 196, 0, 0.95)';
  const toDoTableBackgroundColor = 'rgba(0, 0, 0, 0.7)';
  const toDoTableTextColor = '#FFFFFF';

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'To Do':
        return {
          backgroundColor: toDoTableBackgroundColor,
          color: toDoTableTextColor,
        };
      case 'In Progress':
        return {
          backgroundColor: inProgressSummaryColor,
          color: theme.palette.getContrastText(inProgressSummaryColor),
        };
      case 'In Review':
        return {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.getContrastText(theme.palette.info.main),
        };
      default:
        return {
          backgroundColor: undefined,
          color: theme.palette.getContrastText('transparent'),
        };
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const filteredIssues = issues?.filter(
    (issue: JiraIssueSummary) =>
      issue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.key?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      issue.status.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const {
    total: totalIssues,
    toDo: toDoCount,
    inProgress: inProgressCount,
    inReview: inReviewCount,
  } = issueCounts;

  const toDoWidth = totalIssues > 0 ? (toDoCount / totalIssues) * 100 : 0;
  const inProgressWidth =
    totalIssues > 0 ? (inProgressCount / totalIssues) * 100 : 0;
  const inReviewWidth =
    totalIssues > 0 ? (inReviewCount / totalIssues) * 100 : 0;

  const pageCount = Math.ceil((filteredIssues?.length ?? 0) / ITEMS_PER_PAGE);
  const paginatedIssues = filteredIssues?.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  return (
    <Paper
      style={{
        padding: '0px 2px 10px 2px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: 'calc(100vh - 20px)',
        boxSizing: 'border-box',
      }}
    >
      <Grid
        item
        md={12}
        style={{ paddingLeft: 22, paddingTop: 16, paddingBottom: 10 }}
      />

      <Box px={2.5} py={1}>
        <Typography variant="body2" color="textSecondary">
          Work item status
        </Typography>
        <Typography variant="h6">{totalIssues}</Typography>
        <Box display="flex" alignItems="center" mt={0.5}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '0%',
                bgcolor: toDoSummaryColor,
                mr: 0.5,
              }}
            />
            <Typography variant="body2" style={{ color: toDoSummaryColor }}>
              To Do {toDoCount}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" ml={theme.spacing(1)}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '0%',
                bgcolor: inProgressSummaryColor,
                mr: 0.5,
              }}
            />
            <Typography
              variant="body2"
              style={{
                color: inProgressSummaryColor,
              }}
            >
              In Progress {inProgressCount}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" ml={theme.spacing(1)}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '0%',
                bgcolor: theme.palette.info.main,
                mr: 0.5,
              }}
            />
            <Typography
              variant="body2"
              style={{ color: theme.palette.info.main }}
            >
              In Review {inReviewCount}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 8,
            borderRadius: 4,
            mt: 0.5,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              width: `${toDoWidth}%`,
              bgcolor: toDoSummaryColor,
            }}
          />
          <Box
            sx={{
              width: `${inProgressWidth}%`,
              bgcolor: inProgressSummaryColor,
            }}
          />
          <Box
            sx={{
              width: `${inReviewWidth}%`,
              bgcolor: theme.palette.info.main,
            }}
          />
        </Box>
      </Box>

      <Grid
        container
        alignItems="center"
        justifyContent="flex-end"
        style={{ padding: '0px 20px 10px 20px', paddingTop: 10 }}
      >
        <Grid item>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search issues..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{
              width: '250px',
            }}
          />
        </Grid>
      </Grid>

      <Grid
        item
        md={12}
        style={{
          marginRight: 8,
          marginBottom: 6,
          paddingTop: 0,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        component={({ children }) => (
          <TableContainer
            style={{
              flexGrow: 1,
              overflowY: 'auto',
            }}
          >
            {children}
          </TableContainer>
        )}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Work items</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedIssues?.map((row: JiraIssueSummary) => (
              <TableRow
                key={row.key}
                style={{ backgroundColor: theme.palette.background.paper }}
              >
                <TableCell>
                  <Link
                    href={row.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    underline="hover"
                  >
                    <Typography variant="body2">
                      {row.key} - {row.name}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.project}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.status}
                    size="small"
                    style={{
                      ...getStatusStyles(row.status),
                      borderRadius: '0%',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Box display="flex" justifyContent="center" mt={2} mb={2}>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        )}
      </Box>
    </Paper>
  );
};
